import { extend } from "@dhx/ts-common/core";
import { create, el } from "@dhx/ts-common/dom";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import { locate } from "@dhx/ts-common/html";
import { IHandlers, SelectionEvents } from "@dhx/ts-common/types";
import { View } from "@dhx/ts-common/view";

import { dragManager, DropPosition, Selection, TreeCollection, DataEvents, DragEvents, DropBehaviour, DragMode, IDataEventsHandlersMap, IDragEventsHandlersMap } from "@dhx/ts-data";

import Editor, { EditorEvents, IEditor, IEditorConfig } from "./Editor";
import { keyNavigation } from "./KeyNavigation";
import { Id, ITree, ITreeConfig, ITreeState, SelectStatus, TreeEvents, ITreeEventHandlersMap, ItemIcon, ITreeItem } from "./types";

function getSelectionIndent(level: number): number {
	return level * 20;
}

export class Tree<T extends object = object> extends View implements ITree {
	public config: ITreeConfig;
	public data: TreeCollection<T & ITreeItem>;
	public events: IEventSystem<DataEvents | TreeEvents | DragEvents, IDataEventsHandlersMap & ITreeEventHandlersMap & IDragEventsHandlersMap>;
	public selection: Selection;

	private _editor: IEditor;
	private _handlers: IHandlers;

	private _isSelectionActive: boolean;
	private _root: string;

	private _focusId: string;

	constructor(element: HTMLElement | string, config: ITreeConfig = {}) {
		super(element, extend({
			dropBehaviour: DropBehaviour.child,
			icon: {
				file: "dxi dxi-file-outline",
				folder: "dxi dxi-folder",
				openFolder: "dxi dxi-folder-open"
			}
		}, config));

		const init = (item: ITreeItem) => {
			item.opened = item.opened;
			item.$mark = SelectStatus.unselected;
			item.checkbox = this.config.checkbox;
			item.$autoload = Boolean(item.items && this.config.autoload);
			item.$editor = false;
			return item;
		};

		if (Array.isArray(this.config.data)) {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new TreeCollection({autoload: this.config.autoload, init}, this.events);
			this.data.parse(this.config.data);
		} else if (this.config.data && this.config.data.events) {
			this.data = this.config.data;
			this.data.config.init = init;
			this.events = this.data.events;
			this.events.context = this;
		} else {
			this.events = new EventSystem<DataEvents>(this);
			this.data = new TreeCollection({autoload: this.config.autoload, init}, this.events);
		}

		this._isSelectionActive = true;
		this.selection = new Selection({}, this.data, this.events);

		if (this.config.keyNavigation) {
			keyNavigation.add(this._uid, this);
		}
		this._editor = Editor;

		this._initEvents();
		this._initHandlers();

		if (this.config.dragMode) {
			dragManager.setItem(this._uid, this);
		}
		this._root = this.data.getRoot();

		const render = () => this._draw();
		this.mount(element, create({ render }));
	}
	focusItem(id: string): void {
		this._focusId = id;
		this.data.eachParent(id, item => {
			if (!item.opened) {
				this.expand(item.id);
			}
		});
		this.paint();
	}
	destructor(): void {
		this.events.clear();
		this.unmount();
	}
	editItem(id: string, config?: IEditorConfig): void {
		this.data.update(id, {$edit: true, $editConfig: config});
	}
	getState(): ITreeState {
		const state: ITreeState = {};
		this.data.eachChild(this._root, item => {
			state[item.id] = {
				open: item.opened,
				selected: item.$mark
			};
		}, true);
		return state;
	}
	setState(state: ITreeState): void {
		this.data.eachChild(this._root, item => {
			if (item.id in state) {
				item.opened = state[item.id].open;
				item.$mark = state[item.id].selected;
			}
		}, true);
		this.paint();
	}
	toggle(id: Id): void {
		const item = this.data.getItem(id);
		if (item.$autoload) {
			this.data.loadItems(id);
			this.data.update(id, {
				$autoload: false,
				opened: true
			});
		} else {
			this.data.update(id, {opened: !item.opened});
		}
	}
	getChecked(): Id[] {
		const checked = [];
		this.data.eachChild(this._root, item => {
			if (item.$mark === SelectStatus.selected) {
				checked.push(item.id);
			}
		});
		return checked;
	}
	checkItem(id: Id): void {
		this._updateItemCheck(id, SelectStatus.selected);
	}
	collapse(id: Id): void {
		if (this.data.haveItems(id)) {
			this.data.update(id, { opened: false });
		}
	}
	collapseAll(): void{
		this.data.eachChild(this._root, ({id}) => this.collapse(id), true);
	}
	expand(id: Id): void {
		if (this.data.haveItems(id)) {
			this.data.update(id, { opened: true });
		}
	}
	expandAll(): void {
		this.data.eachChild(this._root, ({id}) => this.expand(id), true);
	}
	uncheckItem(id: Id): void {
		this._updateItemCheck(id, SelectStatus.unselected);
	}
	// TODO: remove sute_7.0
	close(id: Id): void {
		this.collapse(id);
	}
	// TODO: remove sute_7.0
	closeAll(): void {
		this.collapseAll();
	}
	// TODO: remove sute_7.0
	open(id: Id): void {
		this.expand(id);
	}
	// TODO: remove sute_7.0
	openAll(): void {
		this.expandAll();
	}
	// TODO: remove sute_7.0
	unCheckItem(id: Id): void {
		this.uncheckItem(id);
	}
	private _draw() {
		const items = this._drawItems(this.data.getRoot());
		return el("ul", {
			class: "dhx_widget dhx_tree" +
				(!this._isSelectionActive ? " dhx_tree--no-selection " : "") +
				(this.config.css ? " " + this.config.css : ""),
			dhx_widget_id: this._uid,
			...this._handlers
		}, items);
	}
	private _initEvents() {
		this.data.events.on(DataEvents.change, (id, status, obj) => {
			if (status === "remove") {
				this._updateParents(obj.parent, true);
			}
			if (status === "add") {
				this._updateParents(id);
			}
			this.paint();
		});
		this._editor.events.on(EditorEvents.end, (target, id, value) => {
			if (this._uid === target) {
				this.data.update(id, {$edit: false, value});
			}
			keyNavigation.block(false);
		});
		this._editor.events.on(EditorEvents.begin, (id: string) => {
			if (id === this._uid) {
				keyNavigation.block(true);
			}
		});

		this.events.on(DragEvents.beforeDrag, (item, ghost) => {
			const isFolder =  this.config.isFolder ? this.config.isFolder(item) : this.data.haveItems(item.id);
			const iconType = getItemIconType(isFolder, item.opened);
			const icon = (item.icon || this.config.icon)[iconType] || this.config.icon[iconType];
			ghost.innerHTML = `<div class="dhx_tree-list-item__icon ${icon}"></div><span class="dhx_tree-list-item__text">${item.text || item.value}</span>`;
		});
		this.events.on(DragEvents.canDrop, (id, pos) => {
			let dropStatus;
			if (this.config.dropBehaviour === DropBehaviour.complex) {
				if (pos === DropPosition.top) {
					dropStatus = "dhx_tree-drop--top";
				} else if (pos === DropPosition.bot) {
					dropStatus = "dhx_tree-drop--bottom";
				} else {
					dropStatus = "dhx_tree-drop--in-folder";
				}
			} else {
				dropStatus = this.config.dropBehaviour === DropBehaviour.child ? "dhx_tree-drop--in-folder" : "dhx_tree-drop--bottom";
			}
			if (this.data.exists(id)) {
				this.data.update(id, {$drophere: dropStatus});
			}
		});
		this.events.on(DragEvents.cancelDrop, id => {
			if (this.data.exists(id)) {
				this.data.update(id, {$drophere: null});
			}
		});
		this.events.on(DragEvents.dropComplete, (id, pos) => {
			if (this.config.dropBehaviour === DropBehaviour.child || (this.config.dropBehaviour === DropBehaviour.complex && pos === DropPosition.in)) {
				this.expand(id);
			}
		});
		this.events.on(DragEvents.dragStart, () => {
			this._isSelectionActive = false;
			this.paint();
		});
		this.events.on(DragEvents.dragEnd, () => {
			this._isSelectionActive = true;
			this.paint();
		});
		this.events.on(SelectionEvents.afterSelect, id => this._focusId = id);
		if (this.config.editing) {
			this.events.on(TreeEvents.itemDblClick, id => this.editItem(id));
		}
	}
	private _initHandlers() {
		this._handlers = {
			onmouseleave: (e) => {
				dragManager._cancelCanDrop();
			},
			onclick: (e) => {
				const id = locate(e);
				if (!id) {
					return;
				}

				if (e.target.classList.contains("dhx_tree-toggle-button")) {
					this.toggle(id);
					return;
				}

				if (e.target.classList.contains("dhx_tree-checkbox")) {
					const item = this.data.getItem(id);
					if (item.$mark === SelectStatus.unselected) {
						this.checkItem(id);
					} else {
						this.uncheckItem(id);
					}
					return;
				}
				this.events.fire(TreeEvents.itemClick, [id, e]);
				if (this.data.exists(id)) {
					this.selection.add(id);
				}
			},
			ondblclick: (e) => {
				const id = locate(e);
				this.events.fire(TreeEvents.itemDblClick, [id, e]);
			},
			ondragstart(e) {
				e.preventDefault();
			},
			onmousedown: (e) => {
				if (this.config.dragMode && this.config.dragMode !== DragMode.target) {
					dragManager.onMouseDown(e);
				}
			},
			oncontextmenu: (e) => {
				const id = locate(e);
				if (!id) {
					return;
				}
				this.events.fire(TreeEvents.itemRightClick, [id, e]);
				this.events.fire(TreeEvents.itemContextMenu, [id, e]); // TODO: remove sute_7.0
			}
		};
	}
	private _drawItems(parent, level = 0) {
		return this.data.map(item => {
			if (!item) {
				return;
			}
			let toggleButton;
			let childs;
			let select;
			let isFolder = this.config.isFolder ? this.config.isFolder(item) : false;

			if (item.$autoload || this.data.haveItems(item.id)) {
				isFolder = true;
				toggleButton = el("div", { class: "dxi dxi-menu-right dhx_tree-toggle-button"}, "");
				if (item.opened) {
					toggleButton = el("div", { class: "dxi dxi-menu-down dhx_tree-toggle-button dhx_tree-toggle-button--open"}, "");
					childs = this._drawItems(item.id, level + 1);
				}
			}

			if (item.checkbox) {
				const className = item.$mark === SelectStatus.indeterminate ? "dxi-minus-box" :
					(item.$mark === SelectStatus.selected ? "dxi-checkbox-marked" : "dxi-checkbox-blank-outline");
				select = el("div", {
					class: "dhx_tree-checkbox dxi " + className
				});
			}
			const iconType = getItemIconType(isFolder, item.opened);
			const icon = (item.icon || this.config.icon)[iconType] || this.config.icon[iconType];

			let itemValue;
			if (item.$edit) {
				itemValue = this._editor.edit(this._uid, {item, ...item.$editConfig});
			} else {
				itemValue = el("span", { class: "dhx_tree-list-item__text"}, item.text || item.value);
			}
			if (isFolder) {
				return el("li", {
					class: "dhx_tree-list-item dhx_tree-list-item--parent" + (item.css ? " " + item.css : ""),
					dhx_id: item.id,
					_key: item.id
				},
				[
					el("div.dhx_tree-folder", {
						class: (item.id === this._focusId ? " dhx_tree-folder--focused" : "") +
						(item.$selected ? " dhx_tree-folder--selected" : "") +
						(item.$drophere ? (" " + item.$drophere) : ""),
						style: {
							"left": `${-getSelectionIndent(level) }px`,
							"right": `${-getSelectionIndent(level)}px`,
							"margin-left": `${getSelectionIndent(level)}px`
						}
					},
					[
						toggleButton,
						el("div.dhx_tree-list-item__content", [
							select,
							el("div", {class: "dhx_tree-list-item__icon " + icon}),
							itemValue
						]),
				]),
					childs && el("ul.dhx_tree-list", childs)
				]);
			} else {
				return el("li", {
					class: "dhx_tree-list__item dhx_tree-list-item" +
						(item.id === this._focusId ? " dhx_tree-list-item--focused" : "") +
						(item.$selected ? " dhx_tree-list-item--selected" : "") +
						(item.$drophere ? (" " + item.$drophere) : "") +
						(isFolder ? "dhx_tree-list-item--folder" : ""),
					style: {
						"left": `${-getSelectionIndent(level)}px`,
						"right": `${-getSelectionIndent(level)}px`,
						"margin-left": `${getSelectionIndent(level)}px`
					},
					dhx_id: item.id,
					level,
				},
					[
						toggleButton,
						el("div", {
							class: "dhx_tree-list-item__content"
						}, [
								select,
								el("div", { class: "dhx_tree-list-item__icon " + icon }),
								itemValue
							]),
					]);
			}
		}, parent, false);
	}
	private _updateItemCheck(id: Id, status: SelectStatus) {
		this.data.update(id, {$mark: status});
		this.data.eachChild(id, obj => obj.$mark = status); // mark all childs
		this._updateParents(id);
	}
	private _updateParents(id: Id, self = false) {
		if (id === this._root) {
			return;
		}
		this.data.eachParent(id, obj => {
			let selected = 0;
			let unselected = 0;
			this.data.eachChild(obj.id, child => {
				if (!child.checkbox) {
					return;
				}
				switch(child.$mark) {
					case SelectStatus.unselected:
						unselected++;
						break;
					case SelectStatus.selected:
						selected++;
						break;
					default:
						break;
				}
			}, true);
			if (selected !== 0 && unselected !== 0) {
				obj.$mark = SelectStatus.indeterminate;
			} else if (selected === 0 && unselected !== 0) {
				obj.$mark = SelectStatus.unselected;
			} else {
				obj.$mark = SelectStatus.selected;
			}
		}, self);
	}
}

function getItemIconType(isFolder: boolean, isOpened: boolean): ItemIcon {
	if (!isFolder) {
		return ItemIcon.file;
	}
	if (isOpened) {
		return ItemIcon.openFolder;
	}
	return ItemIcon.folder;
}